import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Crypto } from '@services/helpers/encryption-utils';
import { LocalStorageService } from '@services/helpers/local-storage.service';
import { ToastrService } from 'ngx-toastr';

import { CalendarSettingsService } from '@modules/my-calander/calendar/services/calendar-settings.service';

type LinkState = 'loading' | 'ready' | 'off' | 'hidden';

/**
 * The logged-in agent's own booking link for the current project, shown on the project's
 * Appointments page so an agent never has to go into Project Setup to find it.
 *
 * Same shape the External Calendar tab hands out per agent: the project-wide signed token with the
 * agent's `booking_slug` as the second segment — `/book/{project}/{agent}?t=…`.
 */
@Component({
  selector: 'app-my-booking-link',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './my-booking-link.component.html',
  styleUrls: ['./my-booking-link.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyBookingLinkComponent implements OnInit {
  private readonly localStorageService = inject(LocalStorageService);
  private readonly settingsApi = inject(CalendarSettingsService);
  private readonly toast = inject(ToastrService);
  private readonly cdr = inject(ChangeDetectorRef);

  state: LinkState = 'loading';
  url = '';

  ngOnInit(): void {
    const projectId: string = Crypto.decrypt(this.localStorageService.GetItem('pid')) || '';
    if (!projectId) {
      this.state = 'hidden';
      return;
    }

    // `agent_id` omitted on both calls: the server resolves it to the logged-in user.
    this.settingsApi
      .get(projectId)
      .pipe(
        switchMap(settings => {
          if (!settings.publicBookingEnabled || !settings.bookingSlug) return of('');
          return this.settingsApi.getBookingLink(projectId).pipe(
            map(link => this.withAgentSegment(link.url || `${window.location.origin}${link.path}`, settings.bookingSlug)),
            catchError(() => of(''))
          );
        }),
        catchError(() => of(null))
      )
      .subscribe(url => {
        this.url = url || '';
        // Master admins and operations managers set agents up but never take bookings
        // themselves, so "you are not taking bookings" would only be noise for them.
        const role = String(Crypto.decrypt(this.localStorageService.GetItem('role')) || '').toLowerCase();
        const managesOnly = role === 'master admin' || role === 'operations manager';
        this.state = url ? 'ready' : url === null || managesOnly ? 'hidden' : 'off';
        this.cdr.markForCheck();
      });
  }

  copy(): void {
    if (!this.url) return;
    if (!navigator.clipboard) {
      this.toast.info(this.url);
      return;
    }
    navigator.clipboard
      .writeText(this.url)
      .then(() => this.toast.success('Booking link copied'))
      .catch(() => this.toast.info(this.url));
  }

  private withAgentSegment(url: string, slug: string): string {
    const [path, query] = url.split('?');
    return `${path.replace(/\/+$/, '')}/${encodeURIComponent(slug)}${query ? `?${query}` : ''}`;
  }
}
